import React, { memo } from 'react';
import Image from 'next/image';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@emotion/react';
import { Freeform } from 'components/common/Freeform';
import { parseBlocks } from 'services/parseUtils';
import hex2rgba from '../../../services/colorUtils/hex2rgba';
import CustomText from '../../common/subcomponents/Typography/CustomText';

const PrimaryAction = memo(({ action, index }) => {
  const theme = useTheme();
  const layoutOption = action?.imagePosition || 'left';
  const actionTitle = action?.actionTitle;
  const actionBody = action?.actionBody;
  const actionImage = action?.image?.url;

  const renderResources = (resources) =>
    resources?.map((r, index) => (
      <div key={index}>
        {r.fields.url?.href ? (
          <a
            href={r.fields.url.href}
            title={r.fields.title}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'underline' }}
          >
            {r.fields.title}
          </a>
        ) : (
          <span>{r.fields.title}</span>
        )}
      </div>
    ));

  return (
    <Box
      sx={{
        backgroundColor:
          index % 2 === 0
            ? hex2rgba(action?.backgroundColor || '#BFB8AF', '0.15')
            : undefined,
        padding: '1rem',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: { xs: 'auto', sm: '440px' },
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: layoutOption === 'left' ? 'row' : 'row-reverse',
            },
            alignItems: 'center',
            gap: '24px',
            width: '100%',
            height: { xs: 'auto', sm: '360px' },
            ml: 'auto',
            mr: 'auto',
            maxWidth: { sm: '540px', md: '768px', lg: '1180px', xl: '1180px' },
            position: 'relative',
          }}
        >
          <Box
            component="div"
            id="action-image-div"
            sx={{
              overflow: 'hidden',
              marginRight: layoutOption === 'right' ? '16px' : '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              mt: { xs: '0px', sm: '77px' },
              width: { xs: '320px', sm: 'auto' },
              height: { xs: '230px', sm: 'auto' },
            }}
          >
            {actionImage && (
              <Image
                src={actionImage}
                alt={actionTitle}
                height={360}
                width={580}
                style={{
                  borderRadius: '20px',
                }}
              />
            )}
          </Box>
          <Box
            component="div"
            id="action-body-div"
            sx={{ paddingLeft: { xs: '0px', md: '72px' } }}
          >
            <Box sx={{ paddingRight: '0px' }}>
              {actionTitle && (
                <CustomText
                  font="Source Sans Pro"
                  color={theme.palette.gray?.[800] || '#3A3A3A'}
                  fontSize={{ xs: '28px', sm: '40px' }}
                  fontWeight="500"
                  lineHeight="48px"
                  listSpacing="0px"
                >
                  {actionTitle}
                </CustomText>
              )}
            </Box>
            <Box
              sx={{
                width: { xs: '375px', sm: '509px' },
                height: { xs: 'auto', sm: '100px' },
                paddingTop: theme.spacing(2),
              }}
            >
              {actionBody && (
                <Typography
                  sx={{
                    font: 'Source Sans 3',
                    color: theme.palette.gray?.[600] || '#6B6B6B',
                    fontSize: '17px',
                    fontWeight: '400',
                    lineHeight: '24px',
                    listSpacing: '4px',
                  }}
                >
                  {actionBody}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          ml: 'auto',
          mr: 'auto',
          mt: '1rem',
          mb: '1rem',
          maxWidth: {
            xs: '375px',
            sm: '540px',
            md: '768px',
            lg: '1180px',
            xl: '1180px',
          },
        }}
      >
        <Box
          sx={{
            '& ul,ol,p': { marginBottom: '.75rem' },
            '& .free-form-wrapper': { fontSize: '1.3rem' },
          }}
        >
          <Freeform blocks={parseBlocks(action?.actionDescription || [])} />
        </Box>
        {action?.resources?.length > 0 && (
          <Typography
            sx={{
              fontSize: '1.2rem',
              fontWeight: 600,
              paddingTop: '.5rem',
              paddingBottom: '.5rem',
            }}
          >
            Resources:
          </Typography>
        )}
        {renderResources(action?.resources)}
      </Box>
    </Box>
  );
});

PrimaryAction.displayName = 'PrimaryAction';
export default PrimaryAction;
